import {
  createQuery,
  type QueryClient,
  queryOptions,
} from "@tanstack/solid-query";
import type { Accessor } from "solid-js";

/**
 * Shared query/resource primitive for feature slices.
 *
 * Wraps TanStack Query to produce a consistent set of helpers for a single
 * data shape. Routes consume `options()` directly (e.g. for `ensureQueryData`
 * in loaders); reactive components consume `use()`.
 *
 * @template TData   The shape of data returned by the fetcher.
 * @template TParams The parameters required to identify and fetch the resource.
 *
 * @param key       Pure function that derives a stable query key from params.
 * @param fetcher   Async function that fetches the data for the given params.
 * @param staleTime How long (ms) cached data is considered fresh. Defaults to 30 s.
 *
 * @returns An object with helpers bound to this resource's key/fetcher:
 *  - `key`         — raw key factory (useful for manual cache access)
 *  - `options`     — builds a `queryOptions` object (use in route loaders)
 *  - `use`         — reactive hook that runs the query inside a component
 *  - `seed`        — pre-populates the cache unconditionally
 *  - `seedIfEmpty` — pre-populates the cache only when the entry is absent (use in route components)
 *  - `invalidate`  — marks cached entries stale so they refetch on next access
 *  - `remove`      — evicts entries from the cache entirely
 */
export function createResource<TData, TParams>({
  key,
  fetcher,
  staleTime = 30_000,
  placeholderData,
}: {
  key: (params: TParams) => readonly unknown[];
  fetcher: (params: TParams) => Promise<TData>;
  staleTime?: number;
  /**
   * Optional placeholder data used when the query is disabled (e.g. no active
   * business). Without it, a disabled query has `data === undefined`, which
   * makes solid-query's resource suspend the nearest Suspense boundary during
   * SSR — keeping the SSR stream open and the browser tab spinning forever.
   */
  placeholderData?: TData;
}) {
  // Builds a fully-configured queryOptions object for the given params.
  // Pass `enabled = false` to define the query without triggering a fetch.
  const options = (params: TParams, enabled = true) =>
    queryOptions({
      queryKey: key(params),
      queryFn: () => fetcher(params),
      staleTime,
      enabled,
      // We only ever pass plain values (never a function), so cast to any
      // to satisfy TanStack Query's placeholderData union type.
      placeholderData: placeholderData as any,
    });

  // Reactive query hook for use inside Solid components.
  // Accepts signal accessors so the query re-runs when params change.
  const use = (getParams: Accessor<TParams>, getEnabled?: Accessor<boolean>) =>
    createQuery(() => options(getParams(), getEnabled?.() ?? true));

  // Writes data directly into the cache, bypassing a network request.
  // Useful for seeding the cache with data already fetched in a route loader.
  const seed = (qc: QueryClient, params: TParams, data: TData) =>
    qc.setQueryData(key(params), data, { updatedAt: Date.now() });

  // Seeds the cache only when the entry is absent. Use this in route components
  // to hydrate from SSR loader data without overwriting fresher client state
  // (e.g. a mutation that ran before navigation).
  const seedIfEmpty = (qc: QueryClient, params: TParams, data: TData) => {
    if (!qc.getQueryData(key(params))) {
      qc.setQueryData(key(params), data, { updatedAt: Date.now() });
    }
  };

  // Marks the cached entry as stale, triggering a background refetch
  // on the next time the data is observed by an active query.
  const invalidate = (qc: QueryClient, params: TParams) =>
    qc.invalidateQueries({ queryKey: key(params) });

  // Removes the cached entry entirely. Unlike invalidate, observers will
  // immediately refetch rather than showing stale data first.
  const remove = (qc: QueryClient, params: TParams) =>
    qc.removeQueries({ queryKey: key(params) });

  return { key, options, use, seed, seedIfEmpty, invalidate, remove };
}
